import React, { useState } from "react";
import { BookingStatus, canSeeContact } from "../bookingStatus";

interface Props {
  user: any; // guest (host view) or property owner (guest view)
  role: "Host" | "Guest";
  status: BookingStatus;
  onMessage?: () => void;
}

const TENANT_LABELS: Record<string, string> = {
  student: "Student",
  professional: "Professional",
  digital_nomad: "Digital nomad",
  other: "Other",
};

const Fact = ({ label, value }: { label: string; value?: string | null }) =>
  value ? (
    <div>
      <dt className="text-xs text-neutral-500 dark:text-neutral-400">{label}</dt>
      <dd className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{value}</dd>
    </div>
  ) : null;

/**
 * Host / guest card. Before the booking is confirmed it only shows first name,
 * photo and public profile facts. Email and phone are only read when confirmed.
 */
const ProfileCard: React.FC<Props> = ({ user, role, status, onMessage }) => {
  const [aboutOpen, setAboutOpen] = useState(false);
  const confirmed = canSeeContact(status);

  const first = user?.first_name || "";
  const name = confirmed && user?.last_name ? `${first} ${user.last_name}` : first || role;
  const tenant = user?.tenant_type
    ? TENANT_LABELS[String(user.tenant_type).toLowerCase()] ?? user.tenant_type
    : null;
  const languages: string[] = user?.speak_language
    ? String(user.speak_language).split(/,|\band\b|&/i).map((l: string) => l.trim()).filter(Boolean)
    : [];
  const origin = role === "Host" ? user?.live_in : user?.nationality;
  const isGuest = role === "Guest";
  const about: string = user?.about_me || "";
  const longAbout = about.length > 160;

  return (
    <section
      aria-label={`${role} profile`}
      className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-5"
    >
      <div className="flex items-center gap-4">
        {user?.image ? (
          <img src={user.image} alt="" className="h-16 w-16 shrink-0 rounded-full object-cover" />
        ) : (
          <div
            aria-hidden="true"
            className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-orange-100 dark:bg-[#3A1F17] text-2xl font-semibold text-orange-900 dark:text-[#FFB4A0]"
          >
            {name.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{role}</p>
          <h3 className="truncate text-lg font-semibold capitalize text-neutral-900 dark:text-white">{name}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {user?.email_verified && (
              <span className="rounded-full bg-green-100 dark:bg-green-900/40 px-2 py-0.5 text-xs font-semibold text-green-800 dark:text-green-300">
                Verified
              </span>
            )}
            {tenant && (
              <span className="rounded-full bg-neutral-100 dark:bg-neutral-800 px-2 py-0.5 text-xs font-medium text-neutral-700 dark:text-neutral-300">
                {tenant}
              </span>
            )}
          </div>
        </div>
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3">
        <Fact label={role === "Host" ? "Lives in" : "From"} value={origin} />
        {isGuest && <Fact label="Exchange university" value={user?.abroad_university} />}
        {isGuest && <Fact label="Home university" value={user?.home_university} />}
        {isGuest && <Fact label="Major" value={user?.education_major} />}
        <Fact label="Company" value={user?.company_name} />
        <Fact label="Work" value={user?.work_place || user?.work} />
      </dl>

      {languages.length > 0 && (
        <div className="mt-4">
          <p className="mb-1.5 text-xs text-neutral-500 dark:text-neutral-400">Languages</p>
          <div className="flex flex-wrap gap-1.5">
            {languages.map((l) => (
              <span key={l} className="rounded-full bg-neutral-100 dark:bg-neutral-800 px-2.5 py-1 text-xs capitalize text-neutral-800 dark:text-neutral-200">
                {l}
              </span>
            ))}
          </div>
        </div>
      )}

      {about && (
        <div className="mt-4">
          <p className="mb-1 text-xs text-neutral-500 dark:text-neutral-400">About</p>
          <p className={`whitespace-pre-wrap break-words text-sm leading-relaxed text-neutral-800 dark:text-neutral-200 ${!aboutOpen && longAbout ? "line-clamp-3" : ""}`}>
            {about}
          </p>
          {longAbout && (
            <button
              type="button"
              onClick={() => setAboutOpen((o) => !o)}
              className="mt-1 min-h-[44px] text-sm font-medium text-[#C2410C] dark:text-[#FF8A6E] hover:underline"
            >
              {aboutOpen ? "Show less" : "Read more"}
            </button>
          )}
        </div>
      )}

      <div className="mt-4 border-t border-neutral-100 dark:border-neutral-800 pt-4">
        {confirmed ? (
          <dl className="space-y-2 text-sm">
            <div>
              <dt className="text-xs text-neutral-500 dark:text-neutral-400">Email</dt>
              <dd className="break-all font-medium text-neutral-900 dark:text-neutral-100">{user?.email || "Not provided"}</dd>
            </div>
            <div>
              <dt className="text-xs text-neutral-500 dark:text-neutral-400">Phone</dt>
              <dd className="font-medium text-neutral-900 dark:text-neutral-100">{user?.phone || "Not provided"}</dd>
            </div>
          </dl>
        ) : (
          <p className="text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">
            Contact details are shared once your booking is confirmed.
          </p>
        )}
      </div>

      {onMessage && (
        <button
          type="button"
          onClick={onMessage}
          className="mt-4 inline-flex min-h-[44px] w-full items-center justify-center rounded-xl border border-[#C2410C] dark:border-[#FF8A6E] px-4 text-sm font-semibold text-[#C2410C] dark:text-[#FF8A6E] hover:bg-orange-50 dark:hover:bg-[#241512]"
        >
          Message {first || role.toLowerCase()}
        </button>
      )}
    </section>
  );
};

export default ProfileCard;
